import {ChangeDetectionStrategy, Component, computed, inject, signal} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {MatSliderModule} from '@angular/material/slider';
import {Store} from '@ngrx/store';
import * as moment from 'moment-timezone';
import {CSChallengeTask, MetricUpdate} from 'src/app/core/model/project';
import {
  GetChallengeCycleDayAction,
  SaveChallengeMetricValuesAction,
} from 'src/app/core/store/current-sked/current-sked.action';
import {TaskStatuses} from 'src/app/current-sked/shared/task-statuses';
import {CbDialogFooterComponent} from 'src/app/projects/challenge-builder/shared/cb-dialog-parts.component';
import {ScheduleChipType} from 'src/app/projects/challenge-builder/shared/cb-schedule-chip.component';
import {OphButtonModule} from 'src/app/shared/design/oph-button/oph-button.module';
import {OphIconModule} from 'src/app/shared/design/oph-icon/oph-icon.module';
import {OphXButtonComponent} from 'src/app/shared/design/oph-x-button/oph-x-button.component';
import {TimezoneService} from 'src/app/services/timezone.service';
import {TrackerMetricTypes} from 'src/app/trackers/shared/tracker-metric-types';
import {
  durationDisplayToSeconds,
  DurationType,
  secondsToDurationDisplayStrings,
} from 'src/app/shared/utils/duration.util';

export interface DurationValue {
  primary: string;
  secondary: string;
}

export type MetricValue = string | boolean | number | null | DurationValue;

export interface CsChallengesMetricUpdateDialogData {
  task: CSChallengeTask;
  projectId: string;
  challengeTimezone: string;
  /** When set, the dialog will refetch the current day after a successful save. */
  refetchCurrentDay?: {challengeId: string; timestamp: string};
  onSaved?: () => void;
}

@Component({
  selector: 'cs-challenges-metric-update-dialog',
  standalone: true,
  imports: [
    MatSliderModule,
    MatIconModule,
    CbDialogFooterComponent,
    OphButtonModule,
    OphIconModule,
    OphXButtonComponent,
  ],
  templateUrl: './cs-challenges-metric-update-dialog.component.html',
  styleUrl: './cs-challenges-metric-update-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CsChallengesMetricUpdateDialogComponent {
  private readonly store = inject(Store);
  private readonly timezoneService = inject(TimezoneService);
  readonly dialogRef = inject(MatDialogRef<CsChallengesMetricUpdateDialogComponent>);
  readonly data = inject<CsChallengesMetricUpdateDialogData>(MAT_DIALOG_DATA);

  readonly task = this.data.task;
  readonly projectId = this.data.projectId;
  readonly MetricTypes = TrackerMetricTypes;
  readonly TaskStatuses = TaskStatuses;

  edit = signal(false);
  readonly isViewMode = computed(
    () => this.task?.status === TaskStatuses.InProgress && this.task?.schedule !== 'multiple' && !this.edit()
  );
  readonly saveButtonLabel = computed(() => (this.isViewMode() ? 'Edit Entry' : 'Save Entry'));
  readonly primaryActionDisabled = computed(() => {
    if (this.loading()) return true;
    if (this.isViewMode()) return false;
    return !this.hasUnsavedChanges();
  });

  metricValues = signal<Record<string, MetricValue>>({});
  private originalMetricValues: Record<string, MetricValue> = {};
  loading = signal(false);

  readonly stringValues = computed<Record<string, string>>(() => {
    const vals = this.metricValues();
    const result: Record<string, string> = {};
    for (const [k, v] of Object.entries(vals)) {
      result[k] = typeof v === 'string' ? v : '';
    }
    return result;
  });

  readonly booleanValues = computed<Record<string, number | null>>(() => {
    const vals = this.metricValues();
    const result: Record<string, number | null> = {};
    for (const [k, v] of Object.entries(vals)) {
      if (v === true || v === 1) result[k] = 1;
      else if (v === false || v === 0) result[k] = 0;
      else result[k] = null;
    }
    return result;
  });

  readonly durationValues = computed<Record<string, DurationValue>>(() => {
    const vals = this.metricValues();
    const result: Record<string, DurationValue> = {};
    for (const [k, v] of Object.entries(vals)) {
      result[k] = v && typeof v === 'object' ? (v as DurationValue) : {primary: '', secondary: ''};
    }
    return result;
  });

  /** Map of metric id -> normalized duration type option. */
  readonly metricTypeOptionById = computed<Record<string, string>>(() => {
    const metrics = this.task?.metrics ?? [];
    const map: Record<string, string> = {};
    for (const m of metrics) {
      if (m._id) {
        map[m._id] = this.getMetricTypeOption(m);
      }
    }
    return map;
  });

  /** Map of metric id -> true if that metric uses single duration input (hrs or min only). */
  readonly isSingleInputDurationByMetricId = computed<Record<string, boolean>>(() => {
    const typeOptions = this.metricTypeOptionById();
    const map: Record<string, boolean> = {};
    for (const [id, typeOption] of Object.entries(typeOptions)) {
      map[id] = typeOption === 'hrs' || typeOption === 'min';
    }
    return map;
  });

  /** Map of metric id -> {primary, secondary} placeholder strings for duration inputs. */
  readonly durationPlaceholdersById = computed<Record<string, {primary: string; secondary: string}>>(() => {
    const typeOptions = this.metricTypeOptionById();
    const map: Record<string, {primary: string; secondary: string}> = {};
    for (const [id, typeOption] of Object.entries(typeOptions)) {
      map[id] = this.getDurationPlaceholders(typeOption);
    }
    return map;
  });

  /** Map of metric id -> secondary display value. Shows "00" only when primary has a value. */
  readonly durationSecondaryDisplayById = computed<Record<string, string>>(() => {
    const dvs = this.durationValues();
    const map: Record<string, string> = {};
    for (const [id, dv] of Object.entries(dvs)) {
      const secondaryEmpty = !dv.secondary || dv.secondary === '0';
      map[id] = secondaryEmpty ? (dv.primary ? '00' : '') : dv.secondary;
    }
    return map;
  });

  readonly percentageValues = computed<Record<string, number>>(() => {
    const vals = this.metricValues();
    const result: Record<string, number> = {};
    for (const [k, v] of Object.entries(vals)) {
      result[k] = typeof v === 'string' ? Number(v) || 0 : 0;
    }
    return result;
  });

  constructor() {
    this.initializeMetricValues();
  }

  private initializeMetricValues(): void {
    const task = this.task;
    const metrics = task?.metrics ?? [];
    const isInProgress = task?.status === TaskStatuses.InProgress;
    const isMultipleSchedule = task?.schedule === 'multiple';
    const tz = this.data.challengeTimezone || this.timezoneService.getTimeZone();
    const today = moment().tz(tz).startOf('day');
    const initial: Record<string, MetricValue> = {};
    for (const metric of metrics) {
      if (!metric._id) continue;
      const isUpdatedToday = metric.lastUpdatedAt ? moment.tz(metric.lastUpdatedAt, tz).isSame(today, 'day') : false;
      if (isInProgress && !isMultipleSchedule && metric.value !== undefined && isUpdatedToday) {
        const typeOption = this.getMetricTypeOption(metric);
        initial[metric._id] = this.parseMetricValue(metric.value, metric.type, typeOption);
      } else {
        initial[metric._id] = this.defaultMetricValue(metric.type);
      }
    }
    this.metricValues.set(initial);
    this.originalMetricValues = JSON.parse(JSON.stringify(initial));
  }

  getScheduleType(schedule: string): ScheduleChipType {
    const valid: ScheduleChipType[] = ['morning', 'afternoon', 'evening', 'night', 'multiple'];
    return valid.includes(schedule as ScheduleChipType) ? (schedule as ScheduleChipType) : null;
  }

  get durationTypeOption(): string {
    return (
      (this.task?.metrics?.[0] as {durationType?: string})?.durationType ?? this.task?.metrics?.[0]?.typeOption ?? ''
    );
  }

  setStringValue(metricId: string | undefined, value: string): void {
    if (!metricId) return;
    this.metricValues.update(vals => ({...vals, [metricId]: value}));
  }

  setBooleanValue(metricId: string | undefined, value: number | null): void {
    if (!metricId) return;
    this.metricValues.update(vals => ({...vals, [metricId]: value}));
  }

  setDurationPrimary(metricId: string | undefined, value: string): void {
    if (!metricId) return;
    this.metricValues.update(vals => {
      const current = (vals[metricId] as DurationValue) ?? {primary: '', secondary: ''};
      return {...vals, [metricId]: {...current, primary: value}};
    });
  }

  setDurationSecondary(metricId: string | undefined, value: string): void {
    if (!metricId) return;
    this.metricValues.update(vals => {
      const current = (vals[metricId] as DurationValue) ?? {primary: '', secondary: ''};
      return {...vals, [metricId]: {...current, secondary: value}};
    });
  }

  setPercentageValue(metricId: string | undefined, value: string): void {
    if (!metricId) return;
    this.metricValues.update(vals => ({...vals, [metricId]: value}));
  }

  isSingleInputDuration(typeOption: string): boolean {
    return typeOption === 'hrs' || typeOption === 'min';
  }

  getDurationPlaceholders(typeOption: string): {primary: string; secondary: string} {
    if (typeOption === 'hrs/min') return {primary: 'hrs', secondary: 'min'};
    if (typeOption === 'min/sec') return {primary: 'min', secondary: 'sec'};
    return {primary: typeOption, secondary: ''};
  }

  getMetricTypeOption(metric: {typeOption?: string; durationType?: string}): string {
    const raw = (metric as {durationType?: string}).durationType ?? metric.typeOption ?? '';
    const normalize: Record<string, string> = {
      'hrs:min': 'hrs/min',
      'hrs : min': 'hrs/min',
      'min:sec': 'min/sec',
      'min : sec': 'min/sec',
    };
    return normalize[raw] ?? raw;
  }

  toggleBoolean(metricId: string | undefined, value: 0 | 1): void {
    if (!metricId) return;
    const current = this.booleanValues()[metricId];
    this.setBooleanValue(metricId, current === value ? null : value);
  }

  showDiscardConfirmation = signal(false);

  hasUnsavedChanges(): boolean {
    return (this.task.metrics ?? []).some(metric => this.isMetricUpdated(metric));
  }

  private isMetricUpdated(metric: {
    _id?: string;
    type: TrackerMetricTypes;
    typeOption?: string;
    durationType?: string;
  }): boolean {
    if (!metric._id) return false;
    const typeOption = this.getMetricTypeOption(metric);
    const orig = this.serializeMetricValue(this.originalMetricValues[metric._id], metric.type, typeOption);
    const cur = this.serializeMetricValue(this.metricValues()[metric._id], metric.type, typeOption);
    return String(orig ?? '') !== String(cur ?? '');
  }

  onCancel(): void {
    if (this.hasUnsavedChanges()) {
      this.showDiscardConfirmation.set(true);
    } else {
      this.dialogRef.close();
    }
  }

  onConfirmSave(): void {
    this.onSave();
  }

  onConfirmAbandon(): void {
    this.dialogRef.close();
  }

  onPrimaryAction(): void {
    if (this.isViewMode()) {
      this.enterEditMode();
    } else {
      this.onSave();
    }
  }

  enterEditMode(): void {
    this.edit.set(true);
  }

  onSave(): void {
    const updates = this.buildMetricUpdates();
    if (updates.length === 0) {
      this.dialogRef.close(true);
      this.data.onSaved?.();
      return;
    }
    this.loading.set(true);
    this.store.dispatch(
      new SaveChallengeMetricValuesAction({
        projectId: this.projectId,
        updates,
        onSuccess: () => this.onSaveSuccess(),
        onFailure: () => this.onSaveFailure(),
      })
    );
  }

  private onSaveSuccess(): void {
    const refetch = this.data.refetchCurrentDay;
    if (refetch) {
      const finish = () => {
        this.loading.set(false);
        this.dialogRef.close(true);
        this.data.onSaved?.();
      };
      this.store.dispatch(
        new GetChallengeCycleDayAction({
          challengeId: refetch.challengeId,
          timestamp: refetch.timestamp,
          onSuccess: finish,
          onFailure: finish,
        })
      );
    } else {
      this.loading.set(false);
      this.dialogRef.close(true);
      this.data.onSaved?.();
    }
  }

  private onSaveFailure(): void {
    this.loading.set(false);
  }

  private buildMetricUpdates(): MetricUpdate[] {
    const taskId = this.task._id;
    const updates: MetricUpdate[] = [];
    const vals = this.metricValues();
    for (const metric of this.task.metrics ?? []) {
      if (!metric._id || !this.isMetricUpdated(metric)) continue;
      const typeOption = this.getMetricTypeOption(metric);
      const value = this.serializeMetricValue(vals[metric._id], metric.type, typeOption);
      if (value !== undefined) {
        updates.push({taskId, metricId: metric._id, value: value as number});
      }
    }
    return updates;
  }

  private serializeMetricValue(
    v: MetricValue,
    type: TrackerMetricTypes,
    typeOption: string
  ): string | number | undefined {
    if (v === null || v === undefined) return undefined;
    if (type === TrackerMetricTypes.Boolean) {
      if (v === 1 || v === true) return 1;
      if (v === 0 || v === false) return 0;
      return undefined;
    }
    if (type === TrackerMetricTypes.Duration) {
      if (v && typeof v === 'object' && 'primary' in v) {
        const d = v as DurationValue;
        const durationType = this.toDurationType(typeOption);
        const primary = parseInt(d.primary, 10) || 0;
        const secondary = parseInt(d.secondary, 10) || 0;
        return durationDisplayToSeconds(primary, secondary, durationType);
      }
      return undefined;
    }
    if (type === TrackerMetricTypes.Numeric || type === TrackerMetricTypes.Percentage) {
      return typeof v === 'string' ? v : String(v);
    }
    return undefined;
  }

  private parseMetricValue(value: string | number, type: TrackerMetricTypes, typeOption: string): MetricValue {
    if (type === TrackerMetricTypes.Duration) {
      const durationType = this.toDurationType(typeOption);
      const hasSecondary = !this.isSingleInputDuration(typeOption);
      const totalSeconds = typeof value === 'number' ? value : parseInt(String(value), 10);
      if (!isNaN(totalSeconds) && totalSeconds >= 0) {
        return secondsToDurationDisplayStrings(totalSeconds, durationType, hasSecondary);
      }
      // Legacy string format e.g. "1:30" or "90"
      if (typeof value === 'string' && value.includes(':')) {
        const parts = value.split(':');
        return {primary: parts[0] ?? '', secondary: parts[1] ?? ''};
      }
      return {primary: '', secondary: ''};
    }
    if (type === TrackerMetricTypes.Boolean) {
      const str = String(value);
      if (str === '1' || str.toLowerCase() === 'true') return 1;
      if (str === '0' || str.toLowerCase() === 'false') return 0;
      return null;
    }
    return typeof value === 'number' ? String(value) : value;
  }

  private toDurationType(typeOption: string): DurationType {
    const valid: DurationType[] = ['hrs', 'hrs/min', 'min', 'min/sec', 'sec'];
    return valid.includes(typeOption as DurationType) ? (typeOption as DurationType) : 'min/sec';
  }

  private defaultMetricValue(type: TrackerMetricTypes): MetricValue {
    if (type === TrackerMetricTypes.Duration) return {primary: '', secondary: ''};
    if (type === TrackerMetricTypes.Percentage) return '0';
    if (type === TrackerMetricTypes.Boolean) return null;
    return '';
  }
}
